import { Component, EventEmitter, Input, Output } from "@angular/core";
import { Observable, Subject, filter, takeUntil, withLatestFrom } from "rxjs";
import { Constants, fedTaxErrorMap, stateTaxErrorMap, transactionDetailLabels } from "../../utilities/Constants";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { numberOrDecimalOnly, percentAllowedChars, upToThreeDecimalsOnly } from "../../utilities/regex-patterns";
import { ValidateInputService } from "../../services/validate-input.service";
import { TaxInfo } from "../../../account-info-header/models";
import { currencyStringToNumber } from "../../../create-request/utilities/currencyStringToNumber";
import { Store } from "@ngrx/store";
import * as CustomValidators from '../../../shared/utilities/FormValidators';
import { selectEditTweModalState, selectEditTweServiceError, selectEditTweVerifyCallFinished, selectNraIndicator, selectOpenTwePopup } from "../../../account-info-header/reducers";
import { AccountInfoActions } from "../../../account-info-header/actions";
import { ComponentState } from "../../types/ComponentState";
import { ToastrService } from "ngx-toastr";

interface TaxWithholdingsFormGroup {
  fedTaxInput?: FormControl<number | null>;
  stateTaxInput?: FormControl<number | null>;
}

@Component({
  standalone: false,
  selector: 'edit-account-level-twe-modal',
  templateUrl: './edit-account-level-twe-modal.component.html',
  styleUrls: ['./edit-account-level-twe-modal.component.scss']
})
export class EditAccountLevelTweModalComponent {
  @Input() showModal$: Observable<boolean>;
  @Input() taxInfo: TaxInfo | null;
  @Output() showTwePopupModal = new EventEmitter<boolean>();
  destroy$: Subject<boolean> = new Subject();
  modal: EverestModal;
  modalId: string;
  taxWithholdingText: string = Constants.taxWithholdingText;
  updateTaxWithholdingFormGroup = new FormGroup<TaxWithholdingsFormGroup>({});
  percentAllowedChars = percentAllowedChars;
  hasStateTax: boolean;
  editTweModalState$: Observable<ComponentState>;
  errorMessage: string;
  hasError: boolean;
  previousInput: number;
  stateTaxLabel: string = transactionDetailLabels.stateTax;
  nraIndicator: boolean

  constructor(public validationService: ValidateInputService, private store: Store, private toastr: ToastrService) { }

  ngOnInit(): void {
    this.modalId = 'edit-account-level-twe-modal-' + new Date().getTime().toString();

    this.updateTaxWithholdingFormGroup.setControl('fedTaxInput', new FormControl<number | null>(Number(this.fedTaxFormatted), [
      Validators.required,
      CustomValidators.greaterThan99Fed,
      CustomValidators.wholeNumber,
      Validators.maxLength(10)
    ]));

    this.updateTaxWithholdingFormGroup.setControl('stateTaxInput', new FormControl<number | null>(Number(this.stateTaxFormatted), [
      Validators.required,
      CustomValidators.greaterThan99State,
      Validators.maxLength(10)
    ]));

    // Set combined validator on form group - fed + state cannot exceed 99.99%
    this.updateTaxWithholdingFormGroup.setValidators(
      CustomValidators.sumDoesNotExceed99Point99('fedTaxInput', 'stateTaxInput')
    );

    this.editTweModalState$ = this.store.select(selectEditTweModalState);

    this.store.select(selectEditTweVerifyCallFinished)
      .pipe(
        takeUntil(this.destroy$),
        withLatestFrom(this.store.select(selectOpenTwePopup)),
        filter(([verifyCallFinished, _]) => verifyCallFinished)
      ).subscribe(([_, openTwePopup]) => {
        if (!openTwePopup) {
          this.toastr.info(Constants.editTweNoUpdate);
        } else {
          this.showTwePopupModal.emit(true);
        }
        this.hideModal();
      });

    this.store.select(selectEditTweServiceError).pipe(takeUntil(this.destroy$)).subscribe(serviceError => {
      if (serviceError.status.toString().startsWith('4')) {
        this.errorMessage = serviceError.message;
        this.hasError = true;
      } else if (serviceError.status.toString().startsWith('5')) {
        const stateTaxRequiredWhenFedWithheld = serviceError.message?.toUpperCase() === Constants.editTweStateTaxRequiredWhenFedWithheldServiceError;
        const stateTaxBelowMinimum = serviceError.message?.toUpperCase() === Constants.minStateTaxWithholdingError.toUpperCase();
        const toastrMessage = stateTaxRequiredWhenFedWithheld
          ? Constants.editTweErrorStateTaxRequiredWithFedWithholdingMessage
          : stateTaxBelowMinimum
          ? Constants.minStateTaxWithholdingError
          : Constants.editTweError;
        this.errorMessage = Constants.genericServiceError;
        this.hasError = true;
        this.toastr.error(toastrMessage, '', { toastClass: 'ngx-toastr toast-error tall-toastr' });
      } else {
        this.hasError = false;
        this.errorMessage = '';
      }
    });

    this.store.select(selectNraIndicator).pipe(takeUntil(this.destroy$)).subscribe(nraIndicator => {
      this.nraIndicator = nraIndicator ?? false;
    });
  }

  ngAfterViewInit(): void {
    this.modal = document.getElementById(this.modalId) as EverestModal;
    this.showModal$.pipe(takeUntil(this.destroy$)).subscribe((show) => {
      if (show) {
        if (this.taxInfo) {
          this.hasStateTax = this.taxInfo.hasStateTax;
          this.hasStateTax ? this.updateTaxWithholdingFormGroup?.get('stateTaxInput')?.enable()
          : this.updateTaxWithholdingFormGroup?.get('stateTaxInput')?.disable() ;
          this.fedTaxInput.setValue(currencyStringToNumber(this.taxInfo?.federalTaxRate));
          this.stateTaxInput.setValue(currencyStringToNumber(this.taxInfo?.stateTaxRate));
          if (this.taxInfo?.state && !this.nraIndicator) {
            this.stateTaxLabel = `${transactionDetailLabels.stateTax} (${this.taxInfo.state})`;
          }
        }

        this.modal.showModal();
        this.modal.focus();
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  onCancelClick(): void {
    this.hideModal();
  }

  hideModal() {
    this.modal.hideModal(); //in separate function in order to mock it in unit tests
  }

  onApplyUpdateWithholdingClick() {
    if (this.updateTaxWithholdingFormGroup.valid) {
      this.store.dispatch(AccountInfoActions.verifyTaxWithholdings({ fedTaxRate: Number(this.fedTaxInput.value)!, stateTaxRate: Number(this.stateTaxInput.value)! }));
    }
  }

  get fedTaxFormatted(): string {
    return this.taxInfo?.federalTaxRate ?? '';
  }

  get stateTaxFormatted(): string {
    return this.taxInfo?.stateTaxRate ?? '';
  }

  get fedTaxInput(): FormControl<number | null> {
    return this.updateTaxWithholdingFormGroup?.get('fedTaxInput') as FormControl<number | null>;
  }

  get stateTaxInput(): FormControl<number | null> {
    return this.updateTaxWithholdingFormGroup?.get('stateTaxInput') as FormControl<number | null>;
  }

  get fedTaxError(): string | undefined {
    if (this.fedTaxInput && this.fedTaxInput.errors) {
      const errorKeys: string[] = Object.keys(this.fedTaxInput.errors ?? {});
      if (errorKeys.length > 0) {
        return fedTaxErrorMap[errorKeys[0] as keyof typeof fedTaxErrorMap];
      }
    }
  }

  get stateTaxError(): string | undefined {
    if (this.stateTaxInput && this.stateTaxInput.errors) {
      const errorKeys: string[] = Object.keys(this.stateTaxInput.errors ?? {});
      if (errorKeys.length > 0) {
        return stateTaxErrorMap[errorKeys[0] as keyof typeof stateTaxErrorMap];
      }
    }
  }

  get fedAndStateTaxSumError(): string {
    if (this.updateTaxWithholdingFormGroup && this.updateTaxWithholdingFormGroup.errors) {
        const errorKeys: string[] = Object.keys(
            this.updateTaxWithholdingFormGroup.errors ?? {}
        );
        if (errorKeys.length > 0) {
            return 'Update values so that total withholding does not exceed 99.99%.';
        }
    }
    return '';
  }


  onPaste(event: ClipboardEvent) {
    //block non-numeric inputs
    if (event.clipboardData != undefined) {
      let pastedInput = (event.clipboardData.getData('text/plain')).trim();
      if (!numberOrDecimalOnly.test(pastedInput)) {
        event.preventDefault();
      }
    }
  }

  onStateTaxInput() {
    //do not allow state tax input with more than 3 decimal places
    let stringInput = (this.stateTaxInput.value)!.toString();
    if (!upToThreeDecimalsOnly.test(stringInput)) {
      this.stateTaxInput.setValue(this.previousInput);
    }

    this.previousInput = this.stateTaxInput.value!;
  }
}

type EverestModal = HTMLElement & {
  showModal: () => {};
  hideModal: () => {};
};
